import * as React from "react";
import { useState, useContext, useEffect, createContext, useRef } from "react";
import { makeStyles, Theme, createStyles } from "@mui/styles";
import { cloneDeep } from 'lodash'
import { red, green, grey } from '@mui/material/colors';
import { Typography } from '@mui/material';
import { RegionOfInterestContext, MouseSelectionModePan, MouseSelectionModeSelect, CurrentAttachmentContext } from "~common/form/icg-definition-data-provider";
import {HotKeyMappingContext} from "~common/components/app-view/index";
import {
    Tooltip,
    IconButton,
    Slider,
} from "@mui/material";

import FirstPage from '@mui/icons-material/FirstPage';
import LastPage from '@mui/icons-material/LastPage';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';

import { CircularProgress } from '@mui/material';
import { each } from 'lodash'
import Mousetrap from 'mousetrap'

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        imageWell: {
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            marginTop: "5px",
            height: "100%"
        },
        svgContainer: {
            width: "100%",
            height: "100%",
            minHeight: 600,
            cursor: "pointer",
            overflow: "hidden"
        },
        control: {
            margin: 1
        },
        selectedControl: {
            margin: 1,
            backgroundColor: 'rgba(0, 0, 255, 0.1)'
        },
        controlContainer: {
            display: 'inline-block'
        },
        controlSpacer: {
            display: 'inline-block',
            margin: 1,
            minWidth: 18
        },
        sliderContainer: {
            display: 'inline-block',
            width: 100,
            marginLeft: 10,
            marginRight: 10,
            verticalAlign: 'middle',
            color: 'blue'
        },
        controlIcon: {
            fontWeight: 400,
            fontSize: "1rem"
        },
        selectedControlIcon: {
            fontWeight: 400,
            fontSize: "1rem",
            color: 'blue'
        },
        pageCountContainer: {
            textAlign: 'center',
        },
        message: {
            display: "flex",
            justifyContent: "center",
            paddingTop: 50
        },
        errorMessage: {
            display: "flex",
            justifyContent: "center",
            paddingTop: 50,
            color: red[300],
        },
        loading: {
            display: "flex",
            justifyContent: "center",
            paddingTop: 50,
        },
        messageLink: {
            paddingLeft: 15
        },
        selectionBox: {
            fill: "rgba(0, 0, 255, 0.2)",
            stroke: "blue",
            strokeWidth: "1",
            pointerEvents: "none"
        },
        noPointer: {
            pointerEvents: "none"
        }
    })
);




interface ImageViewerProps {
    onDelete?: () => {}
    hideExport?: boolean
    hidePaging?: boolean
}


var dragData: any;

var SHIFT_PCT = 0.125;
var ZOOM_SCALE_FACTOR = 0.5; // 50% zoom shows 1/4 of image area (0.5² = 0.25)


export const ImageViewer: React.FC<ImageViewerProps> = ({
    onDelete,
    hideExport,
    hidePaging
}) => {
    const classes = useStyles();

    const [currentAttachment, setCurrentAttachment, currentPage, setCurrentPage, rotation, setRotation, scaleMode, setScaleMode, roiPath, setRoiPath, icgOCRData, setICGOCRData, rawOCRData, setRawOCRData]= useContext(CurrentAttachmentContext);
    
    const [currentKeyMap, setCurrentKeyMap] = useContext(HotKeyMappingContext);

    const [panOffsetX, setPanOffsetX] = useState(0)
    const [panOffsetY, setPanOffsetY] = useState(0)
    const [scale, setScale] = useState(1)
    const [image, setImage] = useState(undefined)
    const [error, setError] = useState(undefined)
    const [loading, setLoading] = useState(undefined)
    const [localSelectedRegion, setLocalSelectedRegion] = useState(undefined)
    const [shouldZoomToRegion, setShouldZoomToRegion] = useState(true)
    const [zoomScale, setZoomScale] = useState(0.5) // Default 50% (shows 1/4 of image)

    /* Viewer Context */
    const selectionStart = useRef(null);

    const roiContext = useContext(RegionOfInterestContext);

    var selectedRegion: any;
    var setSelectedRegion: Function;
    var regionsOfInterest: any;
    var setRegionsOfInterest: Function;
    var mouseSelectionMode: any;
    var setMouseSelectionMode: Function;
    var mouseModeUserSelectable: any;
    var setMouseModeUserSelectable: Function;
    var minRegionWidth: any;
    var minRegionHeight: any;
    var defaultRegionStyle: any;
    if (roiContext) {
        selectedRegion = roiContext.selectedRegion;
        setSelectedRegion = roiContext.setSelectedRegion;
        regionsOfInterest = roiContext.regionsOfInterest;
        setRegionsOfInterest = roiContext.setRegionsOfInterest;
        setMouseSelectionMode = roiContext.setMouseSelectionMode;
        mouseSelectionMode = roiContext.mouseSelectionMode;
        mouseModeUserSelectable = roiContext.mouseModeUserSelectable;
        setMouseModeUserSelectable = roiContext.setMouseModeUserSelectable;
        minRegionWidth = roiContext.minRegionWidth;
        minRegionHeight = roiContext.minRegionHeight;
        defaultRegionStyle = roiContext.defaultRegionStyle;
    }
    /* Viewer Context */

    var pageCount: any;
    if (currentAttachment) {
        pageCount = currentAttachment.pageCount
    }

    const _calculateTransformData = () => {

        const imageWell = document.getElementById("imageWell")
        var transform = "";
        var center;
        var transOffsetX = panOffsetX || 0;
        var transOffsetY = panOffsetY || 0;

        if (scale !== 1) {
            transform += 'scale(' + scale + ') ';
        }

        if (imageWell && image) {

            if (rotation !== 0) {
                center = _getViewportRelativeCenter();
                if (center) {
                    //Determine the center of the viewport relative to the image.
                    transform += 'rotate(' + rotation + ' ' + center.x + ' ' + center.y + ') ';
                }
            }
        }

        transform += 'translate(' + transOffsetX + ' ' + transOffsetY + ')';

        return {
            transform: transform
        };
    }

    const _getViewportRelativeCenter = () => {
        const imageWell = document.getElementById("imageWell")
        if (imageWell) {
            var svgRect = imageWell.getBoundingClientRect();

            var scl = cloneDeep(scale)

            var scaleWidth = image.width * scl;
            var scaleHeight = image.height * scl;
            var viewportScaleHeight = svgRect.height * (1 / scl);
            var viewportScaleWidth = svgRect.width * (1 / scl);
            var transOffsetX = panOffsetX || 0;
            var transOffsetY = panOffsetY || 0;

            var centerX = viewportScaleWidth / 2 + transOffsetX;
            var centerY = viewportScaleHeight / 2 + transOffsetY;

            if (scaleWidth > svgRect.width) {
                centerX = viewportScaleWidth / 2 + transOffsetX;
            }

            if (scaleHeight > svgRect.height) {
                centerY = viewportScaleHeight / 2 + transOffsetY;
            }
            return {
                x: centerX,
                y: centerY
            };
        }
        return null
    }

    const scaleFitWidth = () => {
        var imageWell = document.getElementById("imageWell")
        if (imageWell) {
            var svgRect = imageWell.getBoundingClientRect()
            var scl = svgRect.width / image.width;
            var scaleHeight = image.height * scl;
            var offsetY = 0;
            if (scaleHeight < svgRect.height) {
                // This image when scaled to fit width is shorter than the view box
                // Let's center the image vertically
                offsetY = (svgRect.height - scaleHeight) / 2;
            }
            setScale(scl)
            setPanOffsetX(0)
            setPanOffsetY(offsetY)
        }
    }

    const getScaleBestFit = (scaleFactor: any) => {
        var imageWell = document.getElementById("imageWell")

        if (imageWell) {

            var svgRect = imageWell.getBoundingClientRect()

            var hScale = svgRect.width / (image || { width: 1 }).width;
            var vScale = svgRect.height / (image || { height: 1 }).height;

            var scl;
            var scaleWidth;
            var scaleHeight;
            var ofsX = 0;
            var ofsY = 0;

            scaleFactor = scaleFactor || 1;
            if (vScale > hScale) {
                scl = hScale * scaleFactor;
                scaleWidth = scl * svgRect.width;
                scaleHeight = scl * svgRect.height;
                if (scaleHeight < svgRect.height) {
                    ofsY = (svgRect.height - scaleHeight) / 2;
                    ofsX = 0;
                }
            } else {
                scl = vScale * scaleFactor;
                scaleWidth = scl * svgRect.width;
                scaleHeight = scl * svgRect.height;
                if (scaleWidth < svgRect.width) {
                    ofsX = (svgRect.width - scaleWidth) / 2;
                    ofsY = 0;
                }
            }

            return { scale: scl, offsetX: ofsX, offsetY: ofsY }
        }
        return null
    }

    const zoomIn = () => {
        var s = scale * 1.25;
        setScale(s)
    }

    const zoomOut = () => {
        var s = scale * 0.80;
        setScale(s)
    }

    const calculateRegionsBoundingBox = () => {
        if (!regionsOfInterest || regionsOfInterest.length === 0 || !image) {
            return null;
        }

        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;

        regionsOfInterest.forEach((region: any) => {
            if (region.x !== undefined && region.y !== undefined && region.width !== undefined && region.height !== undefined) {
                // Convert normalized coordinates (0-1) to pixel coordinates
                const pixelX = region.x * image.width;
                const pixelY = region.y * image.height;
                const pixelWidth = region.width * image.width;
                const pixelHeight = region.height * image.height;
                
                minX = Math.min(minX, pixelX);
                minY = Math.min(minY, pixelY);
                maxX = Math.max(maxX, pixelX + pixelWidth);
                maxY = Math.max(maxY, pixelY + pixelHeight);
            }
        });

        if (minX === Infinity || minY === Infinity || maxX === -Infinity || maxY === -Infinity) {
            return null;
        }

        return {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY
        };
    }

    const zoomToRegions = () => {
        if (!image || !regionsOfInterest || regionsOfInterest.length === 0) {
            return;
        }

        const boundingBox = calculateRegionsBoundingBox();
        if (!boundingBox) {
            return;
        }

        // Calculate target area based on zoom scale factor
        // zoomScale² gives us the fraction of image area to show
        const targetAreaFraction = zoomScale * zoomScale;
        const imageArea = image.width * image.height;
        const targetArea = imageArea * targetAreaFraction;
        const boundingBoxArea = boundingBox.width * boundingBox.height;

        let targetBox = boundingBox;

        // If bounding box is smaller than target area, expand it
        if (boundingBoxArea < targetArea) {
            const aspectRatio = image.width / image.height;
            
            // Calculate new dimensions maintaining aspect ratio
            const targetWidth = Math.sqrt(targetArea * aspectRatio);
            const targetHeight = Math.sqrt(targetArea / aspectRatio);
            
            // Center the expanded box on the original bounding box
            const centerX = boundingBox.x + boundingBox.width / 2;
            const centerY = boundingBox.y + boundingBox.height / 2;
            
            targetBox = {
                x: Math.max(0, centerX - targetWidth / 2),
                y: Math.max(0, centerY - targetHeight / 2),
                width: Math.min(targetWidth, image.width),
                height: Math.min(targetHeight, image.height)
            };

            // Ensure the box doesn't exceed image boundaries
            if (targetBox.x + targetBox.width > image.width) {
                targetBox.x = image.width - targetBox.width;
            }
            if (targetBox.y + targetBox.height > image.height) {
                targetBox.y = image.height - targetBox.height;
            }
        }

        // Now zoom to the target box
        const imageWell = document.getElementById("imageWell");
        if (imageWell) {
            const svgRect = imageWell.getBoundingClientRect();
            
            // Calculate scale to fit the target box in the viewport
            const scaleX = svgRect.width / targetBox.width;
            const scaleY = svgRect.height / targetBox.height;
            const newScale = Math.min(scaleX, scaleY) * 0.9; // 0.9 for some padding

            // Calculate pan offset to center the target box in the viewport
            // Based on the mouse coordinate conversion: imageX = (viewportX - (panOffsetX * scale)) / scale
            // Rearranging: panOffsetX = (viewportX - imageX * scale) / scale
            
            // Target box center in image coordinates
            const targetCenterX = targetBox.x + targetBox.width / 2;
            const targetCenterY = targetBox.y + targetBox.height / 2;
            
            // Viewport center where we want the target center to appear
            const viewportCenterX = svgRect.width / 2;
            const viewportCenterY = svgRect.height / 2;
            
            // Calculate pan offset using the coordinate conversion formula from mouse code
            // From mouse code: imageX = (viewportX - (panOffsetX * scale)) / scale
            // Rearranging: panOffsetX = (viewportX - imageX * scale) / scale
            const newOffsetX = (viewportCenterX - targetCenterX * newScale) / newScale;
            const newOffsetY = (viewportCenterY - targetCenterY * newScale) / newScale;

            setScale(newScale);
            setPanOffsetX(newOffsetX);
            setPanOffsetY(newOffsetY);
        }
    }

    const _mouseDown = (evt: any) => {
        if (mouseSelectionMode === MouseSelectionModePan) {
            dragData = {
                origMouseX: evt.clientX,
                origMouseY: evt.clientY,
                origOffsetX: panOffsetX,
                origOffsetY: panOffsetY
            };
        } else if (mouseSelectionMode === MouseSelectionModeSelect) {
            const { offsetX, offsetY } = evt.nativeEvent;
            const normalizedX = (offsetX - (panOffsetX * scale)) / scale;
            const normalizedY = (offsetY - (panOffsetY * scale)) / scale;
            selectionStart.current = { x: normalizedX, y: normalizedY };

            const normalizedBox = { x: (normalizedX) / image.width, y: (normalizedY) / image.height, width: 0, height: 0, selectionStart: "point" };
            setLocalSelectedRegion(normalizedBox)

        }
    }

    const _mouseMove = (evt: any) => {
        if (mouseSelectionMode === MouseSelectionModePan) {
            if (dragData) {
                var newOffsetX = dragData.origOffsetX + (evt.clientX - dragData.origMouseX) * (1 / scale / 2);
                var newOffsetY = dragData.origOffsetY + (evt.clientY - dragData.origMouseY) * (1 / scale / 2);
                setPanOffsetX(newOffsetX)
                setPanOffsetY(newOffsetY)
            }
        } else if (mouseSelectionMode === MouseSelectionModeSelect && selectionStart.current) {
            const { offsetX, offsetY } = evt.nativeEvent;
            const currentX = (offsetX - (panOffsetX * scale)) / scale;
            const currentY = (offsetY - (panOffsetY * scale)) / scale;
            // const currentX = ((offsetX / scale) - (panOffsetX * scale));
            // const currentY = ((offsetY / scale) - (panOffsetY * scale));
            const startX = Math.min(selectionStart.current.x, currentX);
            const startY = Math.min(selectionStart.current.y, currentY);
            const width = Math.abs((currentX - selectionStart.current.x) / scale);
            const height = Math.abs((currentY - selectionStart.current.y) / scale);

            var selStart;

            if (selectionStart.current){
                if(selectionStart.current.x < currentX && selectionStart.current.y < currentY){ 
                    selStart = "upperLeft"
                } else if(selectionStart.current.x < currentX && selectionStart.current.y > currentY){ 
                    selStart = "lowerLeft"
                } else if(selectionStart.current.x > currentX && selectionStart.current.y < currentY){ 
                    selStart = "upperRight"
                } else if(selectionStart.current.x > currentX && selectionStart.current.y > currentY){
                    selStart = "lowerRight"
                }
            }


            // For consistency, we will normalize the box to the 
            // const normalizedBox = { x: ((startX / scale) - panOffsetX) / image.width, y: ((startY / scale) - panOffsetY) / image.height, width: (width / scale) / image.width, height: (height / scale) / image.height, type: selectionType };
            const normalizedBox = { x: (startX) / image.width, y: (startY) / image.height, width: (width * scale) / image.width, height: (height * scale) / image.height, selectionStart: selStart, userSelection: true };
            setLocalSelectedRegion(normalizedBox)

        }

    }

    const _mouseUp = (evt: any) => {
        if (mouseSelectionMode === MouseSelectionModePan) {
            dragData = undefined;
        } else if (mouseSelectionMode === 'select') {
            if (localSelectedRegion) {
                const { x, y, width, height } = localSelectedRegion;
                const minHeight = (1 / image.width) * minRegionHeight;
                const minWidth = (1 / image.height) * minRegionWidth;
    
                if (width >= minWidth && height >= minHeight) {
                    // Check if the localSelectedRegion already exists in regionsOfInterest
                    const exists = regionsOfInterest.some(region => 
                        region.x === x &&
                        region.y === y &&
                        region.width === width &&
                        region.height === height
                    );
    
                    // Only push if it does not exist already
                    if (!exists) {
                        localSelectedRegion.userSelection = true;
                        regionsOfInterest.push(localSelectedRegion);
                        if (setRegionsOfInterest) {
                            setRegionsOfInterest([...regionsOfInterest]);
                        }
                    }
                }
    
                setLocalSelectedRegion(null);
            }
            selectionStart.current = null;
        }
    };
       

    const _mouseLeave = () => {
        dragData = undefined
        selectionStart.current = null;
    }

    const cancelSelection = () => {
        setLocalSelectedRegion(null);
        selectionStart.current = null;
    }

    const firstPage = () => {
        setCurrentPage(1)
        setRegionsOfInterest([]) // Clear regions of interest when chaging pages 
    }

    const previousPage = () => {
        if (currentAttachment && currentPage - 1 > 0) {
            var p = cloneDeep(currentPage)
            p = currentPage - 1
            setCurrentPage(p)
            setRegionsOfInterest([]) // Clear regions of interest when chaging pages 
        }
    }

    const nextPage = () => {
        if (currentAttachment && currentPage < currentAttachment.pageCount) {
            var p = cloneDeep(currentPage)
            p = currentPage + 1
            setCurrentPage(p)
            setRegionsOfInterest([]) // Clear regions of interest when chaging pages 
        }
    }

    const lastPage = () => {
        if (currentAttachment) {
            setCurrentPage(currentAttachment.pageCount)
            setRegionsOfInterest([]) // Clear regions of interest when chaging pages 
        }
    }

    const scaleBestFit = (e: any) => {
        var bf = getScaleBestFit(1)
        if (bf) {
            setScale(bf.scale)
            setPanOffsetX(bf.offsetX)
            setPanOffsetY(bf.offsetY)
        }
    }

    const _export = () => {
        const { fileName, storageKey } = currentAttachment;
        window.open(`/api/attachment-storage/${storageKey}/raw?fileName=${fileName}`)
    }

    const rotateCounterClockwise = () => {
        var r = cloneDeep(rotation)
        if (r !== 0) {
            r += -90;
        } else {
            r = 270;
        }
        setRotation(r)
    }

    const rotateClockwise = () => {
        var r = cloneDeep(rotation)
        if (r !== 270) {
            r += 90;
        } else {
            r = 0;
        }
        setRotation(r)
    }

    const panLeft = () => {
        var imageWell = document.getElementById("imageWell")
        if (imageWell) {
            var svgRect = imageWell.getBoundingClientRect();
            var scaleWidth = image.width * scale;
            if (scaleWidth > svgRect.width) {
                var x = panOffsetX - image.width / 2 * SHIFT_PCT;
                setPanOffsetX(x)
            }
        }
    }

    const panRight = () => {
        var imageWell = document.getElementById("imageWell")
        if (imageWell) {
            var svgRect = imageWell.getBoundingClientRect();
            var scaleWidth = image.width * scale;
            if (scaleWidth > svgRect.width) {
                var x = panOffsetX + image.width / 2 * SHIFT_PCT;
                setPanOffsetX(x)
            }
        }
    }

    const panUp = () => {
        var imageWell = document.getElementById("imageWell")
        if (imageWell) {
            var svgRect = imageWell.getBoundingClientRect();
            var scaleHeight = image.height * scale;
            if (scaleHeight > svgRect.height) {
                var y = panOffsetY - image.height / 2 * SHIFT_PCT;
                panOffsetY(y)
            }
        }
    }

    const panDown = () => {
        var imageWell = document.getElementById("imageWell")
        if (imageWell) {
            var svgRect = imageWell.getBoundingClientRect();
            var scaleHeight = image.height * scale;
            if (scaleHeight > svgRect.height) {
                var y = panOffsetY + image.height / 2 * SHIFT_PCT;
                setPanOffsetY(y)
            }
        }
    }

    const zoomUpperLeft = () => {
        setScale(2)
        setPanOffsetX(0)
        setPanOffsetY(0)
    }

    const zoomUpperRight = () => {
        var imageWell = document.getElementById("imageWell")
        if (imageWell) {
            var svgRect = imageWell.getBoundingClientRect()
            var bf = getScaleBestFit(2)
            if (bf) {
                setScale(bf.scale)
                setPanOffsetY(0)
                setPanOffsetX(-((image.width - svgRect.width) * bf.scale - svgRect.width * bf.scale))
            }
        }
    }

    const zoomLowerRight = () => {
        var imageWell = document.getElementById("imageWell")
        if (imageWell) {
            var svgRect = imageWell.getBoundingClientRect();
            var bf = getScaleBestFit(2)
            if (bf) {
                setScale(bf.scale)
                setPanOffsetX(-((image.width - svgRect.width) * bf.scale - svgRect.width * bf.scale))
                setPanOffsetY(-((image.height - svgRect.height) * bf.scale - svgRect.height * bf.scale))
            }
        }
    }

    const zoomLowerLeft = () => {
        var imageWell = document.getElementById("imageWell")
        if (imageWell) {
            var svgRect = imageWell.getBoundingClientRect()
            var bf = getScaleBestFit(2)
            if (bf) {
                setScale(bf.scale)
                setPanOffsetX(0)
                setPanOffsetY(-((image.height - svgRect.height) * bf.scale - svgRect.height * bf.scale))
            }
        }
    }

    const isNative = (contentType: string): boolean => {
        if (contentType == "image/tiff" || contentType == "application/pdf" || contentType == "image/png" || contentType == "image/jpeg") {
            return true
        }
        return false
    }

    const handleScaleModeChange = (mode: 'fit-width' | 'fit-height' | 'best-fit' | null) => {
        if (mode) {
            switch (mode.toLowerCase()) {
                case 'fit-width':
                    scaleFitWidth();
                    break;
                case 'best-fit':
                    scaleBestFit(null);
                    // var bf = getScaleBestFit(1);
                    // if (bf) {
                    //     setScale(bf.scale);
                    //     setPanOffsetX(bf.offsetX);
                    //     setPanOffsetY(bf.offsetY);
                    // }
                    break;
                case 'fit-height':
                    var imageWell = document.getElementById("imageWell");
                    if (imageWell) {
                        var svgRect = imageWell.getBoundingClientRect();
                        var vScale = svgRect.height / (image ? image.height : 1);
                        setScale(vScale);
                        setPanOffsetX(0);
                        setPanOffsetY(0);
                    }
                    break;
                default:
                    // If no scale mode is set or unrecognized, do nothing
                    break;      
            }

        }            
    }


    useEffect(() => {
        handleScaleModeChange(scaleMode);
    }, [scaleMode, image]); 


    useEffect(() => {
       
        setLocalSelectedRegion(selectedRegion)
    }, [selectedRegion])

    useEffect(() => {
        if (currentKeyMap && currentKeyMap.length > 0) {
            // The current keymap will be an array.  Loop over it
            for(let keyMap of currentKeyMap) {

                // Split the key action into parts
                var keyParts = keyMap.action.split(".")
                // If the first part, lowercased, is "formcontainer", then we know this is a key map relevent to the form container
                if (keyParts[0].toLowerCase() == "imageviewer" && keyParts.length > 1) {
                  switch (keyParts[1].toLowerCase()) {
                    case "zoomin":
                      zoomIn();
                      break;
                    case "zoomout":
                      zoomOut();
                      break;
                    case "rotatecounterclockwise":
                      rotateCounterClockwise();
                      break;
                    case "rotateclockwise":
                      rotateClockwise();
                      break;
                  }
                  // Only set the current key map to null if the key action was consumed
                  // Set the current Key map to null, to reset for next key
                  setCurrentKeyMap(null);
                  break;


                }
            }

        }

    
    }, [currentKeyMap])

    useEffect(() => {
        if (shouldZoomToRegion && regionsOfInterest && regionsOfInterest.length > 0 && image) {
            zoomToRegions();
        }
    }, [shouldZoomToRegion, regionsOfInterest, image, zoomScale])


    var imageContainer

    if (image) {

        var transformData = _calculateTransformData();

        imageContainer = (
            <svg className={classes.svgContainer}>
                <image
                    xlinkHref={image.url}
                    width={image.width}
                    height={image.height}
                    transform={transformData.transform}
                    onDoubleClick={getScaleBestFit}
                    onMouseDown={_mouseDown}
                    onMouseMove={_mouseMove}
                    onMouseUp={_mouseUp}
                    onMouseLeave={_mouseLeave}
                >
                </image>
                {localSelectedRegion && (
                    <rect
                        className={classes.selectionBox}
                        x={(((localSelectedRegion.x * image.width)) * scale) + (panOffsetX * scale)}
                        y={(((localSelectedRegion.y * image.height)) * scale) + (panOffsetY * scale)}
                        width={(localSelectedRegion.width * image.width) * scale}
                        height={(localSelectedRegion.height * image.height) * scale}
                    />
                )}

                {regionsOfInterest && regionsOfInterest.map((region, index) => {
                    if (region !== selectedRegion) {

                        var transform

                        if (region.labelStyle && region.labelStyle.transform) {
                            transform = region.labelStyle.transform
                        }

                        if (!region.style) {
                            region.style = defaultRegionStyle
                        }

                        return (
                            <g key={index}
                               className={classes.noPointer}
                            >
                                <rect
                                    x={(((region.x * image.width)) * scale) + (panOffsetX * scale)}
                                    y={(((region.y * image.height)) * scale) + (panOffsetY * scale)}
                                    width={(region.width * image.width) * scale}
                                    height={(region.height * image.height) * scale}
                                    style={region.style}
                                    className={classes.noPointer}
                                />
                                {region.label && (
                                <text
                                    x={(((region.x * image.width)) * scale) + (panOffsetX * scale)} // Slight offset for readability
                                    y={(((region.y * image.height)) * scale) + (panOffsetY * scale)} // Slight offset for readability
                                    style={region.labelStyle || region.style}
                                    transform={transform}
                                    className={classes.noPointer}
                                >
                                    {region.label}
                                </text>
                                )}
                            </g>
                        );
                    }
                })}



            </svg>
        )

    } else {
        imageContainer = (<div />)
    }

    if (currentAttachment && !isNative(currentAttachment.contentType)) {
        imageContainer = (
            <Typography variant="body2" className={classes.message}>This document is not natively viewable in the browser, to view this document<a className={classes.messageLink} style={{ cursor: "pointer", color: "#337ab7" }} onClick={_export}>Click here</a></Typography>
        )
    }

    if (loading) {
        imageContainer = (
            <div className={classes.loading}><CircularProgress color={'inherit'} size={40} thickness={3} /></div>
        )
    }

    if (error) {
        imageContainer = (
            <Typography variant="body2" className={classes.errorMessage}>An error has been encountered while loading this image</Typography>
        )
    }


    useEffect(() => {

        const updateScale = (e: any) => {

            e.preventDefault()

            var bf = getScaleBestFit(1)

            var delta = Math.max(-1, Math.min(1, e.wheelDelta || -e.detail))

            var s = cloneDeep(scale)

            if (delta > 0) {
                s = scale + .02;
                setScale(s)
            } else if (delta < 0) {
                s = scale - .02
                if (bf) {
                    if (s > bf.scale) {
                        setScale(s)
                    }
                }
            }
        }

        var imageWell = document.getElementById("imageWell")
        if (imageWell) {
            imageWell.addEventListener("wheel", updateScale)
            return () => {
                if (imageWell) {
                    imageWell.removeEventListener('wheel', updateScale);
                }
                // each(keyBinderMap, (entry: any, keys: any) => {
                //     Mousetrap.unbind(keys)
                // })
            };
        }

    }, [scale, panOffsetX, panOffsetY])

    useEffect(() => {

        if (currentAttachment) {

            setError(false)

            var url: string;

            var cp = cloneDeep(currentPage)
            if (image && image.storageKey != currentAttachment.storageKey) {
                cp = 1
            }
            if (currentAttachment.url) {
                url = currentAttachment.url
                if (!image || (image.url != url)) {

                    setLoading(true)

                    var img = new Image()

                    img.src = url

                    img.onload = function () {

                        if (currentAttachment.rotation) {
                            setRotation(currentAttachment.rotation)
                        } else {
                            setRotation(0)
                        }

                        setImage({
                            storageKey: currentAttachment.storageKey,
                            url: url,
                            width: img.width,
                            height: img.height,
                            rotation: currentAttachment.rotation || 0
                        })
                        setCurrentPage(1)
                        setLoading(false)
                        setScaleMode("best-fit") // Ensure the image fits best in the viewport after loading

                    }

                    img.onerror = (e: any) => {
                        setError(true)
                        setLoading(false)
                    }
                }

            } else {

                // Make sure we are within our bounds for page count, this shouldn't happen but just in case
                // Make sure we are within our bounds for page count, this shouldn't happen but just
                if (cp > currentAttachment.pageCount) {
                    cp = 1
                }
                if (currentAttachment.pageUrlBase) {
                    // Proxy-backed documents (e.g. the IDP docstore) address pages by a
                    // 0-based index under a caller-provided base path, so the modern viewer
                    // works without the attachment-storage subsystem. Additive: attachments
                    // that don't set pageUrlBase keep the attachment-storage URLs below.
                    url = `${currentAttachment.pageUrlBase}/${cp - 1}`
                } else {
                    switch (currentAttachment.contentType) {
                        case "application/pdf":
                        case "image/png":
                        case "image/gif":
                        case "image/jpeg":
                        case "image/tiff":
                            url = `/api/attachment-storage/${currentAttachment.storageKey}/${cp}?monochrome=true`
                            break;
                        default:
                            url = `/api/attachment-storage/${currentAttachment.storageKey}/raw?fileName=${currentAttachment.fileName}`
                            break;
                    }
                }

                if (isNative(currentAttachment.contentType)) {

                    if (!image || (image.url != url)) {

                        setLoading(true)

                        var img = new Image()
                        img.src = url

                        img.onload = function () {
                            setImage({
                                storageKey: currentAttachment.storageKey,
                                url: url,
                                width: img.width,
                                height: img.height,
                                rotation: rotation
                            })
                            setCurrentPage(cp)
                            setLoading(false)
                        }

                        img.onerror = (e: any) => {
                            setError(true)
                            setLoading(false)
                        }
                    }
                }
            }
        } else {
            setImage(undefined)
        }

    }, [currentAttachment, currentPage])

    useEffect(() => {
        if (image) {
            scaleFitWidth()
        }
    }, [image])

    return (
        <div>
            <div className={classes.controls}>
                {!hideExport && <Tooltip disableInteractive enterDelay={200} title="Download Document">
                    <div className={classes.controlContainer}>
                        <IconButton name="downloadDocumentButton" size="large" tabIndex={-1} disabled={false} onClick={_export} disableRipple={true} color="inherit" className={classes.control}>
                            <i className={"fa fa-download " + classes.controlIcon}></i>
                        </IconButton>
                    </div>
                </Tooltip>}
                <Tooltip enterDelay={200} title="Zoom In">
                    <div className={classes.controlContainer}>
                        <IconButton name="zoomInButton" size="large" tabIndex={-1} disabled={false} onClick={zoomIn} disableRipple={true} color="inherit" className={classes.control}>
                            <i className={"fa fa-search-plus " + classes.controlIcon}></i>
                        </IconButton>
                    </div>
                </Tooltip>
                <Tooltip enterDelay={200} title="Zoom Out">
                    <div className={classes.controlContainer}>
                        <IconButton name="zoomOutButton" size="large" tabIndex={-1} disabled={false} onClick={zoomOut} disableRipple={true} color="inherit" className={classes.control}>
                            <i className={"fa fa-search-minus " + classes.controlIcon}></i>
                        </IconButton>
                    </div>
                </Tooltip>
                <Tooltip enterDelay={200} title="Fit To Screen">
                    <div className={classes.controlContainer}>
                        <IconButton name="fitToScreenButton" size="large" tabIndex={-1} disabled={false} onClick={scaleBestFit} disableRipple={true} color="inherit" className={classes.control}>
                            <i className={"fa fa-arrows-alt " + classes.controlIcon}></i>
                        </IconButton>
                    </div>
                </Tooltip>
                <Tooltip enterDelay={200} title="Rotate Left">
                    <div className={classes.controlContainer}>
                        <IconButton name="rotateLeftButton" size="large" tabIndex={-1} disabled={false} onClick={rotateCounterClockwise} disableRipple={true} color="inherit" className={classes.control}>
                            <i className={"fa fa-undo " + classes.controlIcon}></i>
                        </IconButton>
                    </div>
                </Tooltip>
                <Tooltip enterDelay={200} title="Rotate Right">
                    <div className={classes.controlContainer}>
                        <IconButton name="rotateRightButton" size="large" tabIndex={-1} disabled={false} onClick={rotateClockwise} disableRipple={true} color="inherit" className={classes.control}>
                            <i className={"fa fa-redo " + classes.controlIcon}></i>
                        </IconButton>
                    </div>
                </Tooltip>
                {onDelete && <Tooltip enterDelay={200} title="Delete">
                    <div className={classes.controlContainer}>
                        <IconButton size="large" tabIndex={-1} onClick={onDelete} disableRipple={true} color="inherit" className={classes.control}>
                            <i className={"fa fa-trash " + classes.controlIcon}></i>
                        </IconButton>
                    </div>
                </Tooltip>}
                {mouseModeUserSelectable && <div className={classes.controlSpacer}></div>}

                {mouseModeUserSelectable && <Tooltip title="Pan Mode">
                    <div className={classes.controlContainer}>
                        <IconButton
                            onClick={() => setMouseSelectionMode('pan')}
                            className={mouseSelectionMode === 'pan' ? classes.selectedControl : classes.control}
                            style={{ color: mouseSelectionMode === 'pan' ? 'blue' : 'black' }}
                        >
                            <i className={"fa fa-hand-paper " + (mouseSelectionMode === 'pan' ? classes.selectedControlIcon : classes.controlIcon)}></i>
                        </IconButton>
                    </div>
                </Tooltip>}
                {mouseModeUserSelectable && <Tooltip title="Select Mode">
                    <div className={classes.controlContainer}>
                        <IconButton
                            onClick={() => setMouseSelectionMode('select')}
                            className={mouseSelectionMode === 'select' ? classes.selectedControl : classes.control}
                            style={{ color: mouseSelectionMode === 'select' ? 'blue' : 'black' }}
                        >
                            <i className={"fa fa-crosshairs " + classes.controlIcon}></i>
                        </IconButton>
                    </div>
                </Tooltip>}
                {mouseModeUserSelectable && <Tooltip title="Zoom to Region">
                    <div className={classes.controlContainer}>
                        <IconButton
                            onClick={() => setShouldZoomToRegion(!shouldZoomToRegion)}
                            className={shouldZoomToRegion ? classes.selectedControl : classes.control}
                            style={{ color: shouldZoomToRegion ? 'blue' : 'black' }}
                        >
                            <i className={"fa fa-search-plus " + (shouldZoomToRegion ? classes.selectedControlIcon : classes.controlIcon)}></i>
                        </IconButton>
                    </div>
                </Tooltip>}
                {mouseModeUserSelectable && shouldZoomToRegion && (
                    <div className={classes.sliderContainer}>
                        <Slider
                            value={110 - (zoomScale * 100)} // Invert: right = higher zoom
                            min={10}
                            max={100}
                            step={5}
                            onChange={(event, newValue) => {
                                let invertedValue = 110 - (newValue as number);
                                let newZoomScale = invertedValue / 100;
                                setZoomScale(newZoomScale);
                            }}
                            size="small"
                            aria-label="Zoom Scale"
                        />
                    </div>
                )}

            </div>

            <div id="imageWell" style={{ overflow: 'hidden', minHeight: 400, height: window.innerHeight - 280 }} className={classes.imageWell}>

                {imageContainer}

            </div>

            {!hidePaging && <div className={classes.pageCountContainer}>
                <Tooltip enterDelay={200} title="First Page">
                    <div className={classes.controlContainer}>
                        <IconButton size="large" tabIndex={-1} disabled={false} onClick={firstPage} disableRipple={true} color="inherit" className={classes.control}>
                            <FirstPage />
                        </IconButton>
                    </div>
                </Tooltip>
                <Tooltip enterDelay={200} title="Previous Page">
                    <div className={classes.controlContainer}>
                        <IconButton size="large" tabIndex={-1} disabled={false} onClick={previousPage} disableRipple={true} color="inherit" className={classes.control}>
                            <ChevronLeft />
                        </IconButton>
                    </div>
                </Tooltip>
                {currentPage && pageCount &&
                    <span className={classes.controlContainer}>
                        Page {currentPage} of {pageCount}
                    </span>
                }
                <Tooltip enterDelay={200} title="Next Page">
                    <div className={classes.controlContainer}>
                        <IconButton size="large" tabIndex={-1} disabled={false} onClick={nextPage} disableRipple={true} color="inherit" className={classes.control}>
                            <ChevronRight />
                        </IconButton>
                    </div>
                </Tooltip>
                <Tooltip enterDelay={200} title="Last Page">
                    <div className={classes.controlContainer}>
                        <IconButton size="large" tabIndex={-1} disabled={false} onClick={lastPage} disableRipple={true} color="inherit" className={classes.control}>
                            <LastPage />
                        </IconButton>
                    </div>
                </Tooltip>
            </div>}
        </div>
    );
};

export default ImageViewer;